import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Link} from 'shells/link/link';

import {MarketingUpsellBanner} from '../marketing-upsell-banner';

import {BulkEmailStatusText} from './bulk-email-status-text';
import {BulkEmailWarnings} from './bulk-email-warnings';

type Props = {
    error:
        | {
              title: string;
              detail: string;
          }
        | null
        | undefined;
    recipientStats:
        | {
              totalCount: number;
              missingFieldsCount: number;
              missingFieldsHref: string;
              missingEmailsCount: number;
              missingEmailsHref: string;
              hasReceivedTemplateRecentlyCount: number;
          }
        | null
        | undefined;
};

const ERROR_TOO_MANY_RECIPIENTS_TITLE = 'Too many recipients';

export function BulkEmailStatus(props: Props) {
    if (props.error) {
        if (props.error.title === ERROR_TOO_MANY_RECIPIENTS_TITLE) {
            const errorMessage = NutClientConfig.isTrial ? (
                <div>
                    Trials can only send 25 emails at a time from this list. Purchase a Nutshell
                    plan to send without any limits.
                </div>
            ) : (
                <div>
                    You can only send 500 emails at a time from this list. {/* @ts-expect-error */}
                    <Link as={RoutedLink} variant='primary' to='/email-marketing/summary'>
                        Try Nutshell Campaigns
                    </Link>{' '}
                    to send without any limits.
                </div>
            );

            return (
                <FlashBanner type='error' animate={false} hasBorder={true}>
                    {errorMessage}
                </FlashBanner>
            );
        } else {
            return (
                <FlashBanner type='error' animate={false} hasBorder={true}>
                    {props.error.detail}
                </FlashBanner>
            );
        }
    }

    if (!props.recipientStats) {
        return (
            <BulkEmailStatusText
                variant='info'
                text='Choose a template below to preview your email.'
            />
        );
    }

    const {
        missingFieldsCount,
        missingFieldsHref,
        missingEmailsCount,
        missingEmailsHref,
        hasReceivedTemplateRecentlyCount,
    } = props.recipientStats;

    if (shouldShowWarnings(props.recipientStats)) {
        return (
            <BulkEmailWarnings
                missingFieldsCount={missingFieldsCount}
                missingFieldsHref={missingFieldsHref}
                missingEmailsCount={missingEmailsCount}
                missingEmailsHref={missingEmailsHref}
                hasReceivedTemplateRecentlyCount={hasReceivedTemplateRecentlyCount}
            />
        );
    }

    // Everything's a-ok
    return (
        <>
            {props.recipientStats && props.recipientStats.totalCount > 50 ? (
                <MarketingUpsellBanner />
            ) : undefined}
            <BulkEmailStatusText
                variant='success'
                text='Looks good! All of the recipients you’ve selected will receive this email.'
            />
        </>
    );
}

function shouldShowWarnings({
    // @ts-expect-error
    hasReceivedTemplateRecentlyCount,
    // @ts-expect-error
    missingEmailsCount,
    // @ts-expect-error
    missingFieldsCount,
}) {
    return hasReceivedTemplateRecentlyCount > 0 || missingEmailsCount > 0 || missingFieldsCount > 0;
}
